import { IMPORT_MAX_ANALYZE_CHARS } from './importConstants';
import type { ParsedModification } from './importReviewTypes';

/** Normalize Word/Notes paste quirks before detection or local parse. */
export function normalizePasteText(text: string): string {
  return text
    .replace(/\r\n|\r/g, '\n')
    .replace(/\u00a0|\u202f/g, ' ');
}

/** Strip path segments and control characters from uploaded file names. */
export function sanitizeDisplayFileName(name: string): string {
  const base = name.split(/[/\\]/).pop() || name;
  return base
    .split('')
    .filter((char) => {
      const code = char.charCodeAt(0);
      return code >= 32 && code !== 127;
    })
    .join('')
    .slice(0, 120);
}

export function truncateForAnalyze(text: string): { text: string; truncated: boolean } {
  if (text.length <= IMPORT_MAX_ANALYZE_CHARS) {
    return { text, truncated: false };
  }
  return {
    text: text.slice(0, IMPORT_MAX_ANALYZE_CHARS),
    truncated: true,
  };
}

/** True CSV-ish paste: multiple early lines with 2+ commas, not a bullet build list. */
export function looksLikeCsvPaste(text: string): boolean {
  const trimmed = normalizePasteText(text).trim();
  if (!trimmed.includes(',') || !trimmed.includes('\n')) return false;
  if (/^[\s]*[-*•]/m.test(trimmed) && /^[A-Za-z][^:\n]{1,60}:\s*$/m.test(trimmed)) {
    return false;
  }
  const lines = trimmed.split('\n').slice(0, 8);
  let csvLines = 0;
  for (const line of lines) {
    if ((line.match(/,/g) || []).length >= 2) csvLines += 1;
  }
  return csvLines >= 2;
}

/** Sectioned/bulleted build sheet — should use deterministic parse, not AI. */
export function looksLikeBuildListPaste(text: string): boolean {
  const trimmed = normalizePasteText(text).trim();
  if (!trimmed) return false;
  const hasBullets = /^[\s]*[-*•]/m.test(trimmed);
  const hasSections = /^[A-Za-z][^:\n]{1,60}:\s*$/m.test(trimmed);
  const lineCount = trimmed.split('\n').filter((l) => l.trim()).length;
  return (hasBullets && hasSections) || (hasBullets && lineCount >= 20) || lineCount >= 40;
}

/**
 * Lightweight offline parser used when the analyze API fails.
 * Enough to reach review for sectioned/bulleted build lists.
 */
export function parseBuildListClientSide(text: string): ParsedModification[] {
  const normalized = normalizePasteText(text);
  const lines = normalized.split('\n');
  const mods: ParsedModification[] = [];
  let currentCategory = 'Miscellaneous';
  let tempSeq = 0;

  for (const rawLine of lines) {
    const trimmed = rawLine.replace(/\u00a0/g, ' ').trim();
    if (!trimmed) continue;

    if (/^[A-Za-z][^:\n]{1,60}:\s*$/.test(trimmed)) {
      currentCategory = trimmed.replace(/:\s*$/, '').trim() || currentCategory;
      continue;
    }

    const bulletMatch = trimmed.match(/^[-*•]\s+(.*)$/);
    const content = (bulletMatch ? bulletMatch[1] : trimmed).trim();
    if (!content || content.length < 2) continue;
    if (/^\([^)]{1,40}\)$/.test(content)) continue;

    tempSeq += 1;
    mods.push({
      title: content,
      category: currentCategory,
      vendor: '',
      confidence: 0.7,
      original_line: trimmed,
      temporary_id: `item_${tempSeq}`,
      parent_temporary_id: null,
      depth: 0,
      item_type: 'part',
      source: 'client_fallback',
    });
  }

  return mods;
}

export function isFormFieldTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return Boolean(target.isContentEditable);
}

export function formatSessionStatus(status: string): string {
  const normalized = status.toLowerCase();
  if (normalized === 'completed') return 'Completed';
  if (normalized === 'pending') return 'In progress';
  if (normalized === 'failed') return 'Failed';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/** True when the session token maps to a real import_sessions row (undo supported). */
export function isPersistentSession(sessionToken: string): boolean {
  return (
    sessionToken !== '' &&
    sessionToken !== 'fallback_session' &&
    !sessionToken.startsWith('ephemeral_')
  );
}

export function reindexIndexSet(set: Set<number>, removedIndex: number): Set<number> {
  const next = new Set<number>();
  set.forEach((i) => {
    if (i < removedIndex) next.add(i);
    else if (i > removedIndex) next.add(i - 1);
  });
  return next;
}

export function reindexRecord<T>(record: Record<number, T>, removedIndex: number): Record<number, T> {
  const next: Record<number, T> = {};
  Object.entries(record).forEach(([key, value]) => {
    const i = Number(key);
    if (i < removedIndex) next[i] = value;
    else if (i > removedIndex) next[i - 1] = value;
  });
  return next;
}

export function reindexPendingCategoryApply(
  pending: { changedIndex: number; newCategory: string; similarIndices: number[] } | null,
  removedIndex: number
): typeof pending {
  if (!pending) return null;

  const mapIndex = (i: number): number | null => {
    if (i === removedIndex) return null;
    if (i > removedIndex) return i - 1;
    return i;
  };

  const changedIndex = mapIndex(pending.changedIndex);
  if (changedIndex === null) return null;

  const similarIndices = pending.similarIndices
    .map(mapIndex)
    .filter((i): i is number => i !== null);

  return { ...pending, changedIndex, similarIndices };
}
